// The client theme layer. Two independent inputs decide what `data-theme` a scope gets:
//
//   1. A per-viewer PREFERENCE (light | dark), stored in a COOKIE. localStorage/sessionStorage are banned
//      outright by lint (the auth-material rule), so the persisted preference rides a small non-auth cookie —
//      the alternative the ban's own rationale contemplated. It is a DEV-ONLY control for now (ART-174): the
//      dark palette is wired across the whole app so any page CAN go dark, but the toggle that turns it on
//      only exists in non-production lanes until each page is polished, at which point it is promoted to
//      everyone's Settings (a real account-backed setting then). Production reads `"light"` regardless.
//
//   2. A ROUTE FORCE: the Day-of-Show page is ALWAYS dark, at every width, whatever the preference (product
//      owner, 2026-10-08 — it was mobile-only, and its skin is drawn for dark, so desktop DOS was unreadable). That is a fact
//      about the surface, not a choice — so the shell decides it from the route (the `isFullScreenRoute`
//      precedent in mobileTabs.ts), and it overrides the preference for that route only.
//
// `data-theme` is set on `document.documentElement` (see ShellLayout) so portalled overlays (Modal/Sheet
// portal to document.body) and the global body background inherit it; custom properties cascade, so a scope
// with the attribute re-points every `--ac-*` colour for its subtree.
import { create } from "zustand";
import { IS_NON_PRODUCTION } from "../config/env.js";

export type ThemeName = "light" | "dark";

const COOKIE_NAME = "cntrl_theme";

/** Read the stored preference from the cookie. Prod ⇒ always light (a stale value must never darken
 *  production). SSR-safe (no `document` ⇒ light). */
function readStoredPreference(): ThemeName {
  if (!IS_NON_PRODUCTION || typeof document === "undefined") return "light";
  const match = document.cookie.match(/(?:^|;\s*)cntrl_theme=(light|dark)(?:;|$)/);
  return match?.[1] === "dark" ? "dark" : "light";
}

function writeStoredPreference(theme: ThemeName): void {
  if (typeof document === "undefined") return;
  // A per-viewer DEV preference — no PII, no auth material. Site-wide, one year, Lax (never sent cross-site).
  document.cookie = `${COOKIE_NAME}=${theme}; path=/; max-age=31536000; samesite=lax`;
}

interface ThemeState {
  /** The viewer's chosen app-wide theme. Never dark in production (the toggle does not exist there). */
  preference: ThemeName;
  setPreference: (theme: ThemeName) => void;
}

/** The app-wide theme preference. Plain `create` + a manual cookie, mirroring `activeBand.ts`'s plain store. */
export const useThemePreference = create<ThemeState>((set) => ({
  preference: readStoredPreference(),
  setPreference: (preference) => {
    writeStoredPreference(preference);
    set({ preference });
  },
}));

// Routes that are ALWAYS dark regardless of the preference — the whole Day-of-Show FAMILY: the `/dos` tab and
// its per-show sub-screens (VIP / guest list / logistics), which are reached from a DOS show's essentials and
// are dark for the same reason the tab is. A list of anchored matchers so adding a surface is one line; `/dos`
// is anchored so it never matches `/doshboard`, and the `/shows/<id>/…` matcher names the three DOS sub-paths
// explicitly so the plain `/shows` LIST (an ordinary tab) stays light.
const FORCED_DARK_ROUTES: readonly RegExp[] = [
  /^\/dos(?:\/.*)?$/,
  /^\/shows\/[^/]+\/(?:vip|guests|logistics)(?:\/.*)?$/,
];

/** True iff this pathname is a surface that forces dark (independent of the preference). */
export function isForcedDarkRoute(pathname: string): boolean {
  return FORCED_DARK_ROUTES.some((re) => re.test(pathname));
}

/**
 * The theme a scope should actually render: a forced-dark route wins at EVERY width (the DOS skin is drawn for
 * dark); otherwise the viewer's preference. `mobile` no longer decides anything here and is kept on the signature
 * for the callers that pass it.
 */
export function resolveEffectiveTheme(args: {
  pathname: string;
  mobile: boolean;
  preference: ThemeName;
}): ThemeName {
  const { pathname, preference } = args;
  if (isForcedDarkRoute(pathname)) return "dark";
  return preference;
}

/** True where the dev-only theme toggle may be shown. Promotion = deleting this guard at the call site. */
export const THEME_TOGGLE_AVAILABLE = IS_NON_PRODUCTION;

/**
 * Apply the stored app-wide preference to `<html>` at boot, BEFORE React renders, so a dev viewer who chose
 * dark does not flash light on load. Prod resolves to `"light"` (inert). The route-force (DOS) is applied by
 * ShellLayout once routing resolves; this only seeds the app-wide preference.
 */
export function applyStoredThemeAtBoot(): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = readStoredPreference();
}
